import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getMockData, refreshMockData } from '../data/blockchainMockData';

const initialFilters = {
  search: '',
  amountRange: [0, 50],
  timeRange: 'all',
  txTypes: ['inflow', 'outflow'],
  chains: ['BTC', 'ETH', 'SOL', 'MATIC', 'AVAX', 'BNB'],
  riskLevels: ['low', 'medium', 'high'],
  minDegree: 0,
};

const initialLayout = 'force';

export const useGraphStore = create(
  persist(
    (set, get) => ({
      // Data
      nodes: [],
      edges: [],
      filteredNodes: [],
      filteredEdges: [],
      userEdges: [],
      
      // Filters
      filters: initialFilters,
      
      // Layout
      layout: initialLayout,
      neighborhoodMode: false,
      neighborhoodCenter: null,
      magnetMode: false, // Physics/snapping toggle
      
      // View state
      zoom: 1,
      pan: { x: 0, y: 0 },
      selectedNode: null,
      selectedNodes: [],
      hoveredNode: null,
      
      // Zoom functions (set by ForceGraph)
      zoomIn: null,
      zoomOut: null,
      fitView: null,
      resetViewFn: null,
      
      // UI toggles
      sidebarOpen: true,
      showBlockchainIcons: true,
      connectMode: false,
      connectSource: null,
      loading: false,
      error: null,
      
      // History
      history: [],
      historyIndex: -1,
      
      // Initialize data
      initializeData: () => {
        set({ loading: true });
        try {
          const data = getMockData();
          set({
            nodes: data.nodes,
            edges: data.edges,
            filteredNodes: data.nodes,
            filteredEdges: data.edges,
            loading: false,
            error: null
          });
          get().saveToHistory();
        } catch (error) {
          set({ loading: false, error: error.message });
        }
      },
      
      refreshData: () => {
        set({ loading: true });
        try {
          const data = refreshMockData();
          set({
            nodes: data.nodes,
            edges: data.edges,
            userEdges: [],
            selectedNodes: [],
            neighborhoodMode: false,
            loading: false,
            error: null
          });
          get().applyFilters();
        } catch (error) {
          set({ loading: false, error: error.message });
        }
      },
      
      // Filters
      setFilters: (newFilters) => {
        set({ filters: { ...get().filters, ...newFilters } });
        get().applyFilters();
      },
      
      resetFilters: () => {
        set({ filters: initialFilters });
        get().applyFilters();
      },
      
      applyFilters: () => {
        const { nodes, edges, userEdges, filters } = get();
        
        let filteredNodes = nodes.filter(node => {
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesSearch = 
              node.address.toLowerCase().includes(searchLower) ||
              node.label.toLowerCase().includes(searchLower);
            if (!matchesSearch) return false;
          }
          if (!filters.chains.includes(node.chain)) return false;
          if (!filters.riskLevels.includes(node.risk)) return false;
          if (node.degree < filters.minDegree) return false;
          return true;
        });
        
        const filteredNodeIds = new Set(filteredNodes.map(n => n.id));
        
        // Combine original edges with user-created edges
        const allEdges = [...edges, ...userEdges];
        
        let filteredEdges = allEdges.filter(edge => {
          if (!filteredNodeIds.has(edge.source) && !filteredNodeIds.has(edge.source?.id)) return false;
          if (!filteredNodeIds.has(edge.target) && !filteredNodeIds.has(edge.target?.id)) return false;
          if (edge.amount < filters.amountRange[0] || edge.amount > filters.amountRange[1]) return false;
          if (!filters.txTypes.includes(edge.type)) return false;
          
          if (filters.timeRange !== 'all') {
            const edgeDate = new Date(edge.timestamp);
            const now = new Date();
            const diffDays = (now - edgeDate) / (1000 * 60 * 60 * 24);
            if (filters.timeRange === '24h' && diffDays > 1) return false;
            if (filters.timeRange === '7d' && diffDays > 7) return false;
            if (filters.timeRange === '30d' && diffDays > 30) return false;
          }
          return true;
        });
        
        set({ filteredNodes, filteredEdges });
      },
      
      // Layout
      setLayout: (layout) => {
        set({ layout, neighborhoodMode: false, neighborhoodCenter: null });
        get().saveToHistory();
      },
      
      // Neighborhood mode
      toggleNeighborhoodMode: () => {
        const { neighborhoodMode, selectedNodes, selectedNode } = get();
        const centerNode = selectedNodes[0] || selectedNode;
        
        if (!neighborhoodMode && centerNode) {
          set({ neighborhoodMode: true, neighborhoodCenter: centerNode.id });
        } else {
          set({ neighborhoodMode: false, neighborhoodCenter: null });
        }
      },
      
      getNeighborhoodData: () => {
        const { neighborhoodCenter, nodes, edges, userEdges } = get();
        if (!neighborhoodCenter) return { nodes: [], edges: [] };
        
        const allEdges = [...edges, ...userEdges];
        const neighborMap = new Map(); // nodeId -> distance from center
        neighborMap.set(neighborhoodCenter, 0);
        
        // BFS to find nodes within 3 hops
        const queue = [{ id: neighborhoodCenter, dist: 0 }];
        while (queue.length > 0) {
          const { id, dist } = queue.shift();
          if (dist >= 3) continue;
          
          allEdges.forEach(e => {
            const sourceId = typeof e.source === 'string' ? e.source : e.source?.id;
            const targetId = typeof e.target === 'string' ? e.target : e.target?.id;
            
            if (sourceId === id && !neighborMap.has(targetId)) {
              neighborMap.set(targetId, dist + 1);
              queue.push({ id: targetId, dist: dist + 1 });
            }
            if (targetId === id && !neighborMap.has(sourceId)) {
              neighborMap.set(sourceId, dist + 1);
              queue.push({ id: sourceId, dist: dist + 1 });
            }
          });
        }
        
        const neighborNodes = nodes
          .filter(n => neighborMap.has(n.id))
          .map(n => ({ ...n, distance: neighborMap.get(n.id) }));
        
        const neighborEdges = allEdges.filter(e => {
          const sourceId = typeof e.source === 'string' ? e.source : e.source?.id;
          const targetId = typeof e.target === 'string' ? e.target : e.target?.id;
          return neighborMap.has(sourceId) && neighborMap.has(targetId);
        });
        
        return { nodes: neighborNodes, edges: neighborEdges };
      },
      
      // View controls
      setZoom: (zoom) => set({ zoom: Math.max(0.5, Math.min(3, zoom)) }),
      setPan: (pan) => set({ pan }),
      resetView: () => {
        const { resetViewFn } = get();
        if (resetViewFn) resetViewFn();
        else set({ zoom: 1, pan: { x: 0, y: 0 } });
      },
      
      // Magnet mode toggle
      toggleMagnetMode: () => set({ magnetMode: !get().magnetMode }),
      
      // Selection
      setSelectedNode: (node) => set({ selectedNode: node }),
      setHoveredNode: (node) => set({ hoveredNode: node }),
      
      // Multi-select
      addToSelection: (node) => {
        const { selectedNodes } = get();
        if (!selectedNodes.find(n => n.id === node.id)) {
          set({ selectedNodes: [...selectedNodes, node] });
        }
      },
      
      removeFromSelection: (nodeId) => {
        set({ selectedNodes: get().selectedNodes.filter(n => n.id !== nodeId) });
      },
      
      toggleNodeSelection: (node, multiSelect = false) => {
        const { selectedNodes } = get();
        const isSelected = selectedNodes.find(n => n.id === node.id);
        
        if (multiSelect) {
          if (isSelected) {
            set({ selectedNodes: selectedNodes.filter(n => n.id !== node.id) });
          } else {
            set({ selectedNodes: [...selectedNodes, node] });
          }
        } else {
          set({ selectedNodes: isSelected ? [] : [node], selectedNode: isSelected ? null : node });
        }
      },
      
      clearSelection: () => set({ selectedNodes: [], selectedNode: null }),
      
      selectByLasso: (nodeIds) => {
        const { nodes } = get();
        const selected = nodes.filter(n => nodeIds.includes(n.id));
        set({ selectedNodes: selected });
      },
      
      // Freeze/unfreeze selected nodes
      freezeSelected: () => {
        const { selectedNodes, nodes } = get();
        const frozenIds = new Set(selectedNodes.map(n => n.id));
        const updatedNodes = nodes.map(n => 
          frozenIds.has(n.id) ? { ...n, frozen: true } : n
        );
        set({ nodes: updatedNodes });
        get().applyFilters();
      },
      
      unfreezeAll: () => {
        const { nodes } = get();
        const updatedNodes = nodes.map(n => ({ ...n, frozen: false }));
        set({ nodes: updatedNodes });
        get().applyFilters();
      },
      
      // Connect mode
      toggleConnectMode: () => {
        const { connectMode } = get();
        set({ connectMode: !connectMode, connectSource: null });
      },
      
      setConnectSource: (node) => set({ connectSource: node }),
      
      createUserEdge: (targetNode, edgeData) => {
        const { connectSource, userEdges } = get();
        if (!connectSource || !targetNode) return;
        
        const newEdge = {
          id: `user-edge-${Date.now()}`,
          source: connectSource.id,
          target: targetNode.id,
          amount: edgeData.amount || 1,
          type: edgeData.type || 'outflow',
          label: edgeData.label || '',
          timestamp: new Date().toISOString(),
          isUserCreated: true,
          txHash: `user-${Date.now()}`
        };
        
        set({ 
          userEdges: [...userEdges, newEdge],
          connectSource: null,
          connectMode: false
        });
        get().applyFilters();
        get().saveToHistory();
      },
      
      removeUserEdge: (edgeId) => {
        set({ userEdges: get().userEdges.filter(e => e.id !== edgeId) });
        get().applyFilters();
      },
      
      // UI toggles
      toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
      toggleBlockchainIcons: () => set({ showBlockchainIcons: !get().showBlockchainIcons }),
      
      // History
      saveToHistory: () => {
        const { history, historyIndex, layout, zoom, pan, userEdges } = get();
        const newState = { layout, zoom, pan, userEdges: [...userEdges] };
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newState);
        set({ history: newHistory.slice(-20), historyIndex: newHistory.length - 1 });
      },
      
      undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex > 0) {
          const prevState = history[historyIndex - 1];
          set({ ...prevState, historyIndex: historyIndex - 1 });
          get().applyFilters();
        }
      },
      
      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const nextState = history[historyIndex + 1];
          set({ ...nextState, historyIndex: historyIndex + 1 });
          get().applyFilters();
        }
      },
      
      canUndo: () => get().historyIndex > 0,
      canRedo: () => get().historyIndex < get().history.length - 1,
    }),
    {
      name: 'blockchain-graph-storage',
      partialize: (state) => ({
        filters: state.filters,
        layout: state.layout,
        sidebarOpen: state.sidebarOpen,
        showBlockchainIcons: state.showBlockchainIcons,
        userEdges: state.userEdges,
      }),
    }
  )
);

export default useGraphStore;
